import { AddOnRateType } from '@treadinc/horizon-api-spec';
import { isNil } from 'lodash-es';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Settlement } from '~hooks/useSettlements/models';
import { stripPerLoadSuffix } from '~pages/Approvals/ApproveCharges/groupBillingLines';
import { decimalMultiply, invoiceHelpers } from '~pages/Approvals/DriverPay/helpers';
import { toDecimal } from '~utils/decimal';

import {
  draftNumber,
  isDraftChanged,
  isPercentageDraft,
  NewSettlementAddOnChargeDraft,
  SettlementLineDraft,
  SettlementSheetFormValues,
} from './settlementSheetForm';
import {
  getAddOnChargeRate,
  isFreightLineItem,
  isPercentageAddOnCharge,
  SettlementSheetCharge,
  SettlementSheetLines,
  SettlementSheetOrderGroup,
} from './settlementSheetLines';

type SettlementSheetDrafts = Omit<SettlementSheetFormValues, 'notes'>;

type DecimalAmount = ReturnType<typeof toDecimal>;

interface AddOnChargeBase {
  freightQuantity: DecimalAmount;
  freightTotal: DecimalAmount;
  hasLineItemEdits: boolean;
  lineItemsTotal: DecimalAmount;
}

interface LineItemPreview {
  draft: SettlementLineDraft | undefined;
  isEdited: boolean;
  lineItem: InvoiceLineItem;
  totalAmount: number;
}

interface AddOnSubtotalPart {
  addOnId: string | null;
  name: string;
  rateType: AddOnRateType;
  totalAmount: number;
}

interface SettlementSheetAddOnSubtotal {
  key: string;
  name: string;
  rateType: AddOnRateType;
  totalAmount: number;
}

const sumOf = (amounts: Array<number | DecimalAmount>) =>
  amounts.reduce<DecimalAmount>((sum, amount) => sum.plus(amount), toDecimal(0));

const emptyBase = () => ({
  freightQuantity: toDecimal(0),
  freightTotal: toDecimal(0),
  hasLineItemEdits: false,
  lineItemsTotal: toDecimal(0),
});

const mergeBases = (first: AddOnChargeBase, second: AddOnChargeBase) => ({
  freightQuantity: first.freightQuantity.plus(second.freightQuantity),
  freightTotal: first.freightTotal.plus(second.freightTotal),
  hasLineItemEdits: first.hasLineItemEdits || second.hasLineItemEdits,
  lineItemsTotal: first.lineItemsTotal.plus(second.lineItemsTotal),
});

const getLineItemTotal = (
  lineItem: InvoiceLineItem,
  draft: SettlementLineDraft | undefined,
) => {
  if (!draft || !isDraftChanged(draft, lineItem.quantity, lineItem.rate)) {
    return lineItem.totalAmount;
  }

  return isNil(lineItem.rate)
    ? null
    : decimalMultiply(draftNumber(draft.quantity), draftNumber(draft.rate));
};

const getAddOnChargeTotal = (
  addOnCharge: AddOnCharge,
  draft: SettlementLineDraft | undefined,
  base: AddOnChargeBase | null,
) => {
  const rate = getAddOnChargeRate(addOnCharge);
  const isEdited = isDraftChanged(draft, addOnCharge.quantity, rate);
  const dependsOnLineItems = invoiceHelpers.addOnTotalDependsOnLineItems(
    addOnCharge.revenueCostRateType,
  );

  if (isNil(rate) || (!base && dependsOnLineItems)) {
    return addOnCharge.totalAmount;
  }

  const rescalesWithLineItems =
    dependsOnLineItems &&
    Boolean(base?.hasLineItemEdits) &&
    !addOnCharge.manuallyEditedAt;

  if (!isEdited && !rescalesWithLineItems) {
    return addOnCharge.totalAmount;
  }

  const editedRate = draft ? draftNumber(draft.rate) : rate;

  return invoiceHelpers.calculateAddOnChargeTotal(
    addOnCharge.revenueCostRateType,
    draft ? draftNumber(draft.quantity) : addOnCharge.quantity,
    editedRate,
    isPercentageAddOnCharge(addOnCharge) ? editedRate : 0,
    base?.lineItemsTotal.toNumber() ?? 0,
    base?.freightTotal.toNumber() ?? 0,
    base?.freightQuantity.toNumber(),
    addOnCharge.surchargeType,
  );
};

const getNewAddOnChargeTotal = (
  draft: NewSettlementAddOnChargeDraft,
  settlementBase: AddOnChargeBase,
) => {
  const isFlatFuelSurcharge =
    draft.addOn?._entity.addOnRateType === AddOnRateType.FUEL_SURCHARGE &&
    !isPercentageDraft(draft);

  if (!draft.addOn || isFlatFuelSurcharge) {
    return null;
  }

  const rate = draftNumber(draft.rate);

  return invoiceHelpers.calculateAddOnChargeTotal(
    draft.addOn._entity.addOnRateType,
    draftNumber(draft.quantity),
    rate,
    isPercentageDraft(draft) ? rate : 0,
    settlementBase.lineItemsTotal.toNumber(),
    settlementBase.freightTotal.toNumber(),
  );
};

const getChargeBase = (lineItemPreviews: LineItemPreview[]) => {
  const freightLineItems = lineItemPreviews.filter(({ lineItem }) =>
    isFreightLineItem(lineItem),
  );

  return {
    freightQuantity: sumOf(
      freightLineItems.map(({ draft, lineItem }) =>
        draft ? draftNumber(draft.quantity) : lineItem.quantity,
      ),
    ),
    freightTotal: sumOf(freightLineItems.map(({ totalAmount }) => totalAmount)),
    hasLineItemEdits: lineItemPreviews.some(({ isEdited }) => isEdited),
    lineItemsTotal: sumOf(lineItemPreviews.map(({ totalAmount }) => totalAmount)),
  };
};

const previewLineItem = (lineItem: InvoiceLineItem, drafts: SettlementSheetDrafts) => {
  const draft = drafts.lineItems[lineItem.id];
  const previewTotal = getLineItemTotal(lineItem, draft);

  return {
    draft,
    isEdited: isDraftChanged(draft, lineItem.quantity, lineItem.rate),
    lineItem,
    previewTotal,
    totalAmount: previewTotal ?? lineItem.totalAmount,
  };
};

const previewAddOnCharge = (
  addOnCharge: AddOnCharge,
  drafts: SettlementSheetDrafts,
  base: AddOnChargeBase | null,
) => ({
  addOnCharge,
  totalAmount: getAddOnChargeTotal(
    addOnCharge,
    drafts.addOnCharges[addOnCharge.id ?? ''],
    base,
  ),
});

const previewCharge = (charge: SettlementSheetCharge, drafts: SettlementSheetDrafts) => {
  const lineItems = charge.lineItems.map((lineItem) => previewLineItem(lineItem, drafts));
  const base = getChargeBase(lineItems);
  const addOnCharges = charge.addOnCharges.map((addOnCharge) =>
    previewAddOnCharge(addOnCharge, drafts, base),
  );

  return {
    addOnCharges,
    base,
    charge,
    lineItems,
    totalAmount: base.lineItemsTotal
      .plus(sumOf(addOnCharges.map(({ totalAmount }) => totalAmount)))
      .toNumber(),
  };
};

const getBasesByInvoiceId = (chargePreviews: Array<ReturnType<typeof previewCharge>>) =>
  chargePreviews.reduce((basesByInvoiceId, { base, charge }) => {
    const invoiceId = charge.invoice.id ?? '';

    return basesByInvoiceId.set(
      invoiceId,
      mergeBases(basesByInvoiceId.get(invoiceId) ?? emptyBase(), base),
    );
  }, new Map<string, AddOnChargeBase>());

const previewOrderGroup = (
  { charges, jobAddOnCharges }: SettlementSheetOrderGroup,
  invoiceByLineId: SettlementSheetLines['invoiceByLineId'],
  drafts: SettlementSheetDrafts,
) => {
  const chargePreviews = charges.map((charge) => previewCharge(charge, drafts));
  const basesByInvoiceId = getBasesByInvoiceId(chargePreviews);
  const jobAddOnChargePreviews = jobAddOnCharges.map((addOnCharge) => {
    const invoiceId = invoiceByLineId.get(addOnCharge.id ?? '')?.id ?? '';

    return previewAddOnCharge(
      addOnCharge,
      drafts,
      basesByInvoiceId.get(invoiceId) ?? null,
    );
  });

  return { chargePreviews, jobAddOnChargePreviews };
};

const getAddOnSubtotalName = ({ loadId, name }: AddOnCharge) =>
  loadId ? stripPerLoadSuffix(name) : name;

const getAddOnSubtotals = (parts: AddOnSubtotalPart[]) => [
  ...parts
    .reduce((subtotalsByKey, { addOnId, name, rateType, totalAmount }) => {
      const key = `${addOnId ?? name}-${rateType}`;
      const subtotal = subtotalsByKey.get(key);

      return subtotalsByKey.set(key, {
        key,
        name: subtotal?.name ?? name,
        rateType,
        totalAmount: toDecimal(subtotal?.totalAmount).plus(totalAmount).toNumber(),
      });
    }, new Map<string, SettlementSheetAddOnSubtotal>())
    .values(),
];

export function getSettlementSheetTotals(
  settlement: Settlement,
  { invoiceByLineId, orderGroups }: SettlementSheetLines,
  settlementAddOnCharges: AddOnCharge[],
  drafts: SettlementSheetDrafts,
) {
  const orderGroupPreviews = orderGroups.map((orderGroup) =>
    previewOrderGroup(orderGroup, invoiceByLineId, drafts),
  );
  const chargePreviews = orderGroupPreviews.flatMap(
    ({ chargePreviews: orderGroupChargePreviews }) => orderGroupChargePreviews,
  );
  const lineItemPreviews = chargePreviews.flatMap(({ lineItems }) => lineItems);
  const settlementBase = chargePreviews.reduce(
    (base, chargePreview) => mergeBases(base, chargePreview.base),
    emptyBase(),
  );
  const addOnChargePreviews = [
    ...orderGroupPreviews.flatMap(
      ({ chargePreviews: orderGroupChargePreviews, jobAddOnChargePreviews }) => [
        ...orderGroupChargePreviews.flatMap(({ addOnCharges }) => addOnCharges),
        ...jobAddOnChargePreviews,
      ],
    ),
    ...settlementAddOnCharges.map((addOnCharge) =>
      previewAddOnCharge(addOnCharge, drafts, settlementBase),
    ),
  ];
  const newAddOnChargeTotals = drafts.newAddOnCharges.map((draft) =>
    getNewAddOnChargeTotal(draft, settlementBase),
  );
  const pricedNewAddOnCharges = drafts.newAddOnCharges.flatMap(({ addOn }, index) => {
    const totalAmount = newAddOnChargeTotals[index];

    return addOn && totalAmount !== null ? [{ addOn: addOn._entity, totalAmount }] : [];
  });
  const lineItemsDelta = sumOf(
    lineItemPreviews.map(({ lineItem, totalAmount }) =>
      toDecimal(totalAmount).minus(lineItem.totalAmount),
    ),
  );
  const addOnChargesDelta = sumOf([
    ...addOnChargePreviews.map(({ addOnCharge, totalAmount }) =>
      toDecimal(totalAmount).minus(addOnCharge.totalAmount),
    ),
    ...pricedNewAddOnCharges.map(({ totalAmount }) => totalAmount),
  ]);

  return {
    addOnChargeTotals: new Map(
      addOnChargePreviews.flatMap(({ addOnCharge, totalAmount }) =>
        addOnCharge.id ? [[addOnCharge.id, totalAmount] as const] : [],
      ),
    ),
    addOnSubtotals: getAddOnSubtotals([
      ...addOnChargePreviews.map(({ addOnCharge, totalAmount }) => ({
        addOnId: addOnCharge.addOnId,
        name: getAddOnSubtotalName(addOnCharge),
        rateType: addOnCharge.revenueCostRateType,
        totalAmount,
      })),
      ...pricedNewAddOnCharges.map(({ addOn, totalAmount }) => ({
        addOnId: addOn.id,
        name: addOn.name,
        rateType: addOn.addOnRateType,
        totalAmount,
      })),
    ]),
    addOnsTotal: addOnChargesDelta
      .plus(
        settlement.rebillAddOnChargesTotalAmount ?? settlement.addOnChargesTotalAmount,
      )
      .toNumber(),
    chargeTotals: new Map(
      chargePreviews.map(({ charge, totalAmount }) => [charge.key, totalAmount] as const),
    ),
    hasTotalsPendingSave: lineItemPreviews.some(
      ({ previewTotal }) => previewTotal === null,
    ),
    lineItemTotals: new Map(
      lineItemPreviews.map(
        ({ lineItem, previewTotal }) => [lineItem.id, previewTotal] as const,
      ),
    ),
    newAddOnChargeTotals,
    subtotal: lineItemsDelta
      .plus(settlement.rebillInvoicesTotalAmount ?? settlement.invoicesTotalAmount)
      .toNumber(),
    taxTotal:
      settlement.rebillInvoicesTaxTotalAmount ??
      settlement.invoicesTaxTotalAmount ??
      null,
    total: lineItemsDelta
      .plus(addOnChargesDelta)
      .plus(settlement.rebillTotalAmount ?? settlement.totalAmount)
      .toNumber(),
  };
}

export type SettlementSheetTotalAmounts = ReturnType<typeof getSettlementSheetTotals>;

export function sumShownLineItemTotals(
  lineItems: InvoiceLineItem[],
  lineItemTotals: Map<string, number | null>,
) {
  const totals = lineItems.map((lineItem) =>
    lineItemTotals.has(lineItem.id)
      ? (lineItemTotals.get(lineItem.id) ?? null)
      : lineItem.totalAmount,
  );

  if (totals.some((total) => total === null)) {
    return null;
  }

  return totals
    .reduce((sum, total) => sum.plus(toDecimal(total)), toDecimal(0))
    .toNumber();
}
